import classNames from 'classnames';
import React from 'react';
import { IconAdd, IconMinus } from './icons';
import styles from './number_input.css';

export type NumberValue = number | { invalid: string };

export function NumberInput({
  value,
  onChange,
  step = 1,
  min,
  max,
}: {
  value: NumberValue;
  onChange?(newValue: NumberValue): void;
  step?: number;
  min: number;
  max: number;
}) {
  const valueAsNumber = typeof value === 'number' ? value : 0;

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const num = parseInt(raw, 10);
      const value =
        isNaN(num) || num < min || num > max ? { invalid: raw } : num;
      onChange?.(value);
    },
    [onChange, min, max],
  );
  const doStep = React.useCallback(
    (dir: 1 | -1) => {
      const newValue = valueAsNumber + dir * step;
      onChange?.(Math.min(max, Math.max(min, newValue)));
    },
    [valueAsNumber, min, max, step, handleChange],
  );

  return (
    <span
      className={classNames(
        styles.container,
        typeof value !== 'number' && styles.invalid,
      )}
      title={
        typeof value !== 'number'
          ? `Please enter a number between ${min} and ${max}`
          : undefined
      }
      onClick={React.useCallback((e: React.MouseEvent) => {
        const input = e.currentTarget.querySelector('input');
        if (document.activeElement !== input) {
          input?.focus();
        }
      }, [])}
    >
      <input
        className={styles.input}
        value={typeof value === 'number' ? value : value.invalid}
        onKeyDown={React.useCallback(
          (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              doStep(1);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              doStep(-1);
            }
          },
          [doStep],
        )}
        onChange={handleChange}
      />
      <button
        className={styles.stepper}
        onClick={React.useCallback(() => doStep(-1), [doStep])}
        disabled={valueAsNumber <= min}
      >
        <IconMinus />
      </button>
      <button
        className={styles.stepper}
        onClick={React.useCallback(() => doStep(1), [doStep])}
        disabled={valueAsNumber >= max}
      >
        <IconAdd />
      </button>
    </span>
  );
}
